/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// RN + NativeWind mirror of `apps/web/src/features/contacts/
// ContactDetailView.tsx`. Right pane of the contacts route — when a
// contact is selected from the list, show their profile card with
// quick actions (Message, Audio call, Video call) and a Remove
// contact button. Matches Figma file Y95LG1oFzU2PHIvFuMYTfJ →
// Contacts → "Profile" frame (2804:86011).
//
// Conversion notes:
//   - The web file fetched the user via `searchUsers` inside a
//     useEffect. v2 is presentation-only — the host owns the lookup
//     and passes `user` + `isLoading` via props.
//   - `<div>` -> `<View>`, `<h2>/<h3>/<p>/<span>` -> `<Text>`,
//     `<button>` -> `<Pressable>`.
//   - `lucide-react` -> `lucide-react-native`. Icons take a `color`
//     prop because RN-side icons don't inherit `currentColor`.

import {
	AtSign,
	Building2,
	type LucideIcon,
	MessageSquare,
	MoreHorizontal,
	Phone,
	Star,
	UserMinus,
	Video,
} from "lucide-react-native";
import { Pressable, ScrollView, Text, View } from "react-native";
import { ContactAvatar } from "./ContactAvatar";
import type {
	ContactDetailViewProps,
	ContactDirectoryUser,
} from "./ContactsScreen.types";

export function ContactDetailView(
	props: ContactDetailViewProps,
): JSX.Element {
	const {
		user,
		isLoading,
		onMessageRequested,
		onAudioCall,
		onVideoCall,
		onRemoveContact,
		onMorePress,
		isFavourite = false,
		onToggleFavourite,
	} = props;

	if (isLoading || user === null) {
		return (
			<View className="flex-1 bg-light-surface-primary dark:bg-[#0A0B0F]">
				<View className="flex flex-row items-center gap-md px-xl py-lg border-b border-light-border-secondary dark:border-white/[0.04]">
					<View className="w-12 h-12 rounded-full bg-light-surface-tertiary dark:bg-white/[0.06]" />
					<View className="flex flex-col gap-[6px] flex-1">
						<View className="h-[14px] rounded bg-light-surface-tertiary dark:bg-white/[0.06]" style={{ width: "45%" }} />
						<View className="h-[12px] rounded bg-light-surface-tertiary dark:bg-white/[0.06]" style={{ width: "30%" }} />
					</View>
				</View>
				<View className="flex flex-row gap-sm px-xl py-lg">
					{Array.from({ length: 3 }).map((_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
						<View key={i} className="flex-1 h-16 rounded-xl bg-light-surface-tertiary dark:bg-white/[0.06]" />
					))}
				</View>
				{(["55%", "70%", "45%", "60%"] as const).map((w, i) => (
					<View
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
						key={i}
						className="flex flex-row items-center gap-md px-xl py-md border-b border-light-border-subtle dark:border-white/[0.04]"
					>
						<View className="w-5 h-5 rounded bg-light-surface-tertiary dark:bg-white/[0.06]" />
						<View className="h-[13px] rounded bg-light-surface-tertiary dark:bg-white/[0.06]" style={{ width: w }} />
					</View>
				))}
			</View>
		);
	}

	return (
		<ScrollView
			accessibilityLabel={`${user.displayName} profile`}
			className="flex-1 min-w-0 min-h-0 bg-light-surface-primary dark:bg-[#0A0B0F]"
		>
			<View
				className="
					flex flex-row items-center justify-between
					px-xl py-lg border-b border-light-border-secondary dark:border-white/[0.04]
				"
			>
				<Text className="font-sans font-bold text-headline text-light-text-primary dark:text-white">
					Profile
				</Text>
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="More actions"
					onPress={() => onMorePress?.(user.id)}
					className="
						w-9 h-9 rounded-lg flex items-center justify-center
						hover:bg-light-surface-tertiary dark:hover:bg-white/[0.06]
					"
				>
					<MoreHorizontal size={18} strokeWidth={1.75} color="#888d96" />
				</Pressable>
			</View>

			<View className="flex flex-col items-center px-xl pt-xl gap-md">
				<ContactAvatar
					name={user.displayName}
					imageUri={user.avatarUri}
					size="xl"
					presence={user.presence ?? null}
				/>
				<View className="flex flex-col items-center gap-xs">
					<Text className="font-sans font-bold text-title text-light-text-primary dark:text-white text-center">
						{user.displayName}
					</Text>
					{(user.role !== undefined || user.company !== undefined) && (
						<Text className="font-sans text-footnote text-light-text-secondary dark:text-white/55 text-center">
							{joinNonEmpty([user.role, user.company], " · ")}
						</Text>
					)}
				</View>

				<View className="flex flex-row items-center gap-sm pt-sm">
					<QuickAction
						icon={MessageSquare}
						label="Message"
						tone="primary"
						onPress={() => onMessageRequested(user.id)}
					/>
					<QuickAction
						icon={Phone}
						label="Audio call"
						onPress={
							onAudioCall !== undefined
								? () => onAudioCall(user.id)
								: undefined
						}
					/>
					<QuickAction
						icon={Video}
						label="Video call"
						onPress={
							onVideoCall !== undefined
								? () => onVideoCall(user.id)
								: undefined
						}
					/>
					{onToggleFavourite !== undefined && (
						<QuickAction
							icon={Star}
							label={isFavourite ? "Remove from favourites" : "Add to favourites"}
							tone={isFavourite ? "primary" : "neutral"}
							onPress={() => onToggleFavourite(user.id)}
						/>
					)}
				</View>
			</View>

			<View className="flex flex-col gap-xs px-xl py-xl">
				{/* Only show a handle when it's a real one. Directory /
				    conversation-derived contacts set `username` to the opaque
				    account id, which would render as garbage (e.g.
				    "@eFJncVFP…") — skip it in that case. */}
				{user.username.length > 0 && user.username !== user.id && (
					<DetailRow
						icon={AtSign}
						label="Username"
						value={`@${user.username}`}
					/>
				)}
				{user.company !== undefined && (
					<DetailRow icon={Building2} label="Company" value={user.company} />
				)}
			</View>

			<View
				className="
					flex flex-col gap-xs px-xl py-md
					border-t border-light-border-secondary dark:border-white/[0.04]
				"
			>
				<Pressable
					accessibilityRole="button"
					accessibilityLabel="Remove contact"
					onPress={() => onRemoveContact?.(user.id)}
					// `transition-colors` dropped.
					className="
						flex flex-row items-center gap-md px-md py-md rounded-lg
						hover:bg-light-accent-rose/10 dark:hover:bg-light-accent-rose/10
					"
				>
					<UserMinus size={18} strokeWidth={1.75} color="#f43f5e" />
					<Text className="font-sans text-footnote font-semibold text-light-accent-rose">
						Remove contact
					</Text>
				</Pressable>
			</View>
		</ScrollView>
	);
}

interface QuickActionProps {
	readonly icon: LucideIcon;
	readonly label: string;
	readonly tone?: "neutral" | "primary";
	readonly onPress?: (() => void) | undefined;
}

function QuickAction({
	icon: Icon,
	label,
	tone = "neutral",
	onPress,
}: QuickActionProps): JSX.Element {
	const isPrimary = tone === "primary";
	return (
		<Pressable
			accessibilityRole="button"
			accessibilityLabel={label}
			onPress={onPress}
			disabled={onPress === undefined}
			className={`
				w-12 h-12 rounded-full flex items-center justify-center
				${
					isPrimary
						? "bg-light-accent-sky hover:opacity-90"
						: "bg-light-surface-secondary hover:bg-light-surface-tertiary dark:bg-white/[0.06] dark:hover:bg-white/[0.10]"
				}
			`}
		>
			<Icon
				size={20}
				strokeWidth={1.75}
				color={isPrimary ? "#FFFFFF" : "#1F2937"}
			/>
		</Pressable>
	);
}

interface DetailRowProps {
	readonly icon: LucideIcon;
	readonly label: string;
	readonly value: string;
}

function DetailRow({ icon: Icon, label, value }: DetailRowProps): JSX.Element {
	return (
		<View
			className="
				flex flex-row items-center gap-md px-md py-md rounded-lg
				bg-light-surface-secondary dark:bg-white/[0.04]
			"
		>
			<Icon size={18} strokeWidth={1.75} color="#888d96" />
			<View className="flex-1 min-w-0 flex flex-col">
				<Text className="font-sans text-caption text-light-text-tertiary dark:text-white/55">
					{label}
				</Text>
				<Text
					numberOfLines={1}
					className="font-sans text-footnote text-light-text-primary dark:text-white"
				>
					{value}
				</Text>
			</View>
		</View>
	);
}

function joinNonEmpty(
	parts: readonly (string | undefined)[],
	sep: string,
): string {
	return parts.filter((p): p is string => p !== undefined && p.length > 0).join(
		sep,
	);
}

// Re-exported as a convenience for tests / hosts that need the
// detail-view-specific user shape.
export type { ContactDirectoryUser };
